import { useState } from 'react';
import { InlineLoading } from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { DataTable } from 'components/data-table';
import { AutoRefreshControls } from 'pages/admin/auto-refresh-controls';
import { count } from 'pages/admin/shared/formatters';
import { DEFAULT_POLLING_INTERVAL } from 'pages/admin/utils';
import { queueColumns, runningColumns } from './columns';
import { FailureScan } from './failure-scan';
import { useTimedQueuesSummary } from './hooks';
import { QueueScan } from './queue-scan';
import { ErrorNote } from 'pages/admin/scan-note';
import { TIER_QUEUE_NAMES } from './utils';
import styles from './page.module.scss';

/**
 * Read-only admin view of the timed Sidekiq queues: depth, latency and paused
 * flag per queue, the jobs running now, an on-demand job-class scan of one
 * queue, and an on-demand breakdown of the retry and dead sets.
 *
 * @return The page.
 */
export function SidekiqTimedQueuesPage() {
  const [pollingInterval, setPollingInterval] = useState<number>(
    DEFAULT_POLLING_INTERVAL
  );
  const summary = useTimedQueuesSummary(pollingInterval);
  const queueNames =
    summary.data?.queues.map((queue) => queue.name) ?? TIER_QUEUE_NAMES;

  /**
   * Renders the polled part of the page: queues, set sizes, running jobs.
   *
   * @return The summary sections, or their loading or error state.
   */
  const renderSummary = () => {
    if (summary.isPending) {
      return <InlineLoading description="Loading queue stats..." />;
    }

    // A failed background refresh keeps the last good data on screen (React
    // Query keeps `data`); only a failure with nothing loaded replaces it.
    const { data, error } = summary;
    if (!data) {
      return (
        <ErrorNote
          message={`Error loading queue stats: ${error?.message ?? 'Unknown error'}`}
        />
      );
    }

    return (
      <>
        {error && (
          <ErrorNote
            message={`The last refresh failed (${error.message}); showing the stats from ${new Date(data.generatedAt).toLocaleTimeString()}.`}
          />
        )}
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>Timed Queues</h4>
          <p className={styles.sectionDescription}>
            Latency is the age of the oldest job in the queue (time since it was
            enqueued). A tier queue is Warning past half its SLA budget and
            Breaching past all of it; a lane with no SLA (compute) is judged on
            depth instead: Warning at 5,000 jobs, Breaching at 50,000.
          </p>
          <DataTable
            data={data.queues}
            columns={queueColumns}
            getRowId={(row) => row.name}
          />
          {data.strandedQueues.length > 0 && (
            <>
              <h4 className={styles.sectionTitle}>
                Undrained Queues with Jobs
              </h4>
              <p className={styles.sectionDescription}>
                These queues hold jobs, but no Sidekiq dyno drains them, so the
                jobs never run and the latency only grows. Shown read-only; they
                cannot be scanned here.
              </p>
              <DataTable
                data={data.strandedQueues}
                columns={queueColumns}
                getRowId={(row) => row.name}
              />
            </>
          )}
          <div className={styles.summaryCards}>
            {[
              { label: 'Enqueued', value: count(data.global.enqueued) },
              { label: 'Busy', value: count(data.global.busy) },
              { label: 'Processes', value: count(data.global.processes) },
              { label: 'Processed', value: count(data.global.processed) },
              { label: 'Failed', value: count(data.global.failed) },
              { label: 'Default Queue', value: data.global.defaultQueue },
            ].map(({ label, value }) => (
              <div key={label} className={styles.summaryCard}>
                <div className={styles.summaryLabel}>{label}</div>
                <div className={styles.statValue}>{value}</div>
              </div>
            ))}
          </div>
          <div className={styles.summaryCards}>
            <div className={styles.summaryCard}>
              <div className={styles.summaryLabel}>Retry Set</div>
              <div className={styles.summaryValue}>{count(data.retrySize)}</div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryLabel}>Dead Set</div>
              <div className={styles.summaryValue}>{count(data.deadSize)}</div>
            </div>
            <div className={styles.summaryCard}>
              <div className={styles.summaryLabel}>Scheduled Set</div>
              <div className={styles.summaryValue}>
                {count(data.scheduledSize)}
              </div>
            </div>
          </div>
          <p className={styles.sectionDescription}>
            Scheduled jobs (for example ones enqueued with a wait, or parked by
            Sidekiq Enterprise limiters) are counted here but not scanned.
          </p>
        </section>

        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>Running Now</h4>
          <p className={styles.sectionDescription}>
            Jobs executing on every Sidekiq process, grouped by queue and job
            class. Read from the Sidekiq heartbeat, so up to a few seconds old.
          </p>
          <DataTable
            data={data.running}
            columns={runningColumns}
            getRowId={(row) => `${row.queue}|${row.jobClass}`}
            config={{ sorting: true }}
          />
        </section>
      </>
    );
  };

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Sidekiq Timed Queues"
        actions={null}
      />
      <div className={styles.pageContent}>
        <p className={styles.description}>
          Read-only view of the timed Sidekiq queues: what is waiting, what is
          running, and what has failed. The queue table and running jobs refresh
          automatically; the job-class and failure scans run only when you ask.
        </p>

        {renderSummary()}

        {/* Outside the data branch, so a failed first load can be retried by hand. */}
        <AutoRefreshControls
          generatedAt={summary.data?.generatedAt ?? null}
          onRefresh={() => void summary.refetch()}
          refreshing={summary.isFetching}
          pollingInterval={pollingInterval}
          onPollingIntervalChange={setPollingInterval}
        />

        <QueueScan queueNames={queueNames} />
        <FailureScan />
      </div>
    </>
  );
}
